import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { deleteEvent, getEvents } from "../../../services/EventService";
import { getMe } from "../../../services/AuthService";
import Swal from "sweetalert2";
import { confirm } from "../common/Confirm/confirm";
import { useAuth } from "../../../context/AuthContext";
import { toast } from '../common/Toast/toast';
import NewCustomDataTable from "../common/NewCustomDataTable.jsx";
import NewCustomAsyncSelectDropdown from "../common/NewCustomAsyncSelectDropdown.jsx";
import { getBatches } from "../../../services/BatchService.js";
import { getTrainers } from "../../../services/TrainerService.js";
import debounce from "lodash/debounce";
import { getPriorityMeta, normalizePriority } from "../../../utils/eventPriority";
import AddEventModal from "../AppMenu/Event_Form/AddEventForm.jsx";
import PopupDone, { usePopupDone } from "../common/PopupDone/PopupDone.jsx";
import "../Trainer/TrainerDashboard.css";
import "../AppMenu/Calendar/AdminCalendar/CalendarWithPopup.css";
import "../common/lms-pages.css";
import FilterSheet from "../common/FilterSheet/FilterSheet";
import useDebouncedCallback from "../common/useDebouncedCallback";

// Trainers and batches arrive comma-joined in a single string; "N/A"
// stands in for none.
const splitNames = (value) => {
  const v = String(value || "").trim();
  if (!v || v === "N/A") return [];
  return v.split(",").map((x) => x.trim()).filter(Boolean);
};

const ListEvent = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [currentUser, setCurrentUser] = useState(null);
  const [isTrainer, setIsTrainer] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedTrainerFilter, setSelectedTrainerFilter] = useState(null);
  const [selectedBatchFilter, setSelectedBatchFilter] = useState(null);
  const [appliedTrainerFilter, setAppliedTrainerFilter] = useState("");
  const [appliedBatchFilter, setAppliedBatchFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("name");
  const [details, setDetails] = useState(null);
  const [detailsTab, setDetailsTab] = useState("details");
  const [formModal, setFormModal] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uiSearchTerm, setUiSearchTerm] = useState("");

  // Read before the fetch awaits anything: a reset mid-flight would turn an
  // append into a replace and snap the list back to its first page.
  const appendRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const listEndRef = useRef(null);

  // Dropdown states
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [loadingTrainers, setLoadingTrainers] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [hasLoadedTrainers, setHasLoadedTrainers] = useState(false);
  const [hasLoadedBatches, setHasLoadedBatches] = useState(false);
  const [trainerSearchTerm, setTrainerSearchTerm] = useState("");
  const [batchSearchTerm, setBatchSearchTerm] = useState("");
  const [trainerPage, setTrainerPage] = useState(1);
  const [batchPage, setBatchPage] = useState(1);
  const [hasMoreTrainers, setHasMoreTrainers] = useState(true);
  const [hasMoreBatches, setHasMoreBatches] = useState(true);
  const isFetchingTrainers = useRef(false);
  const isFetchingBatches = useRef(false);

  const navigate = useNavigate();
  const hasLoaded = useRef(false);
  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();

  // Permission check
  useEffect(() => {
    if (!hasPermission("list-event-form", "view", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to view Events.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // ---------- Helper formatting functions ----------
  const formatDateToDMY = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const formatDateTimeWithAmPm = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const formattedDate = date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      });
      const formattedTime = date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      });
      return `${formattedDate}, ${formattedTime}`;
    } catch {
      return "Invalid Date";
    }
  };

  const formatTimeIST = (timeString) => {
    if (!timeString) return "N/A";
    try {
      if (timeString.includes("T") || timeString.includes("Z")) {
        const timeObj = new Date(timeString);
        const hours = timeObj.getUTCHours();
        const minutes = timeObj.getUTCMinutes();
        const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        const ampm = hours >= 12 ? "PM" : "AM";
        return `${hour12}:${minutes.toString().padStart(2, "0")} ${ampm}`;
      } else if (timeString.includes(":")) {
        const [hours, minutes] = timeString.split(":").map(Number);
        const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        const ampm = hours >= 12 ? "PM" : "AM";
        return `${hour12}:${minutes.toString().padStart(2, "0")} ${ampm}`;
      }
      return timeString;
    } catch {
      return timeString;
    }
  };

  const getTrainerNameFromPopulated = (trainerField) => {
    if (!trainerField) return "N/A";
    if (Array.isArray(trainerField)) {
      const names = trainerField.map((t) => t?.trainerName || t?.name || "").filter(Boolean);
      return names.length ? names.join(", ") : "N/A";
    }
    return String(trainerField);
  };

  const getBatchNameFromPopulated = (batchField) => {
    if (!batchField) return "N/A";
    if (Array.isArray(batchField)) {
      const names = batchField.map((b) => b?.name || "").filter(Boolean);
      return names.length ? names.join(", ") : "N/A";
    }
    return String(batchField);
  };

  const getVenueNameFromPopulated = (venueField) => {
    if (!venueField) return "N/A";
    if (Array.isArray(venueField)) {
      const names = venueField.map((v) => v?.classroomNo || v?.name || "").filter(Boolean);
      return names.length ? names.join(", ") : "N/A";
    }
    if (typeof venueField === "object") {
      return venueField.classroomNo || venueField.name || "N/A";
    }
    return String(venueField);
  };

  // ---------- Trainer dropdown (unchanged) ----------
  const fetchTrainers = useCallback(async (search = "", pageNum = 1) => {
    try {
      isFetchingTrainers.current = true;
      setLoadingTrainers(true);
      const response = await getTrainers(20, pageNum, search);
      const trainers = response.data || [];
      const hasMore = trainers.length === 20;
      return { trainers, hasMore };
    } catch (err) {
      console.error("Error fetching trainers:", err);
      return { trainers: [], hasMore: false };
    } finally {
      setLoadingTrainers(false);
      isFetchingTrainers.current = false;
    }
  }, []);

  const loadTrainers = useCallback(
    async (search = "", pageNum = 1, append = false) => {
      const { trainers, hasMore } = await fetchTrainers(search, pageNum);
      const newOptions = trainers.map((t) => ({
        value: t.trainerId || t.id,
        label: t.trainerName ? `${t.trainerName} (${t.trainerEmail || ""})` : t.trainerEmail || "Unknown Trainer",
        trainer: t,
      }));
      if (append) {
        setTrainerOptions((prev) => [...prev, ...newOptions]);
      } else {
        setTrainerOptions(newOptions);
      }
      setHasMoreTrainers(hasMore);
      return { trainers, hasMore };
    },
    [fetchTrainers]
  );

  const handleLoadMoreTrainers = useCallback(async () => {
    if (!hasMoreTrainers || loadingTrainers || isFetchingTrainers.current) return;
    const nextPage = trainerPage + 1;
    setTrainerPage(nextPage);
    await loadTrainers(trainerSearchTerm, nextPage, true);
  }, [hasMoreTrainers, loadingTrainers, trainerPage, trainerSearchTerm, loadTrainers]);

  const handleTrainerSearch = useMemo(
    () => debounce(async (inputValue) => {
      setTrainerSearchTerm(inputValue);
      setTrainerPage(1);
      await loadTrainers(inputValue, 1, false);
    }, 700),
    [loadTrainers]
  );

  const handleTrainerDropdownOpen = useCallback(async () => {
    if (!hasLoadedTrainers && trainerOptions.length === 0 && !loadingTrainers) {
      await loadTrainers("", 1, false);
      setHasLoadedTrainers(true);
    }
  }, [hasLoadedTrainers, trainerOptions.length, loadingTrainers, loadTrainers]);

  // ---------- Batch dropdown (fetches only when opened, using current trainer ID) ----------
  const fetchBatches = useCallback(async (search = "", pageNum = 1, trainerId = null) => {
    try {
      isFetchingBatches.current = true;
      setLoadingBatches(true);
      // trainerId will be passed to getBatches (null = all batches)
      const response = await getBatches(20, pageNum, search, "all", trainerId);
      const batches = response.data || [];
      const hasMore = batches.length === 20;
      return { batches, hasMore };
    } catch (err) {
      console.error("Error fetching batches:", err);
      return { batches: [], hasMore: false };
    } finally {
      setLoadingBatches(false);
      isFetchingBatches.current = false;
    }
  }, []);

  const loadBatches = useCallback(
    async (search = "", pageNum = 1, append = false, trainerId = null) => {
      const { batches, hasMore } = await fetchBatches(search, pageNum, trainerId);
      const newOptions = batches.map((b) => ({
        value: b.id,
        label: b.name.length > 50 ? `${b.name.substring(0, 50)}...` : b.name,
        batch: b,
      }));
      if (append) {
        setBatchOptions((prev) => [...prev, ...newOptions]);
      } else {
        setBatchOptions(newOptions);
      }
      setHasMoreBatches(hasMore);
      return { batches, hasMore };
    },
    [fetchBatches]
  );

  const handleLoadMoreBatches = useCallback(async () => {
    if (!hasMoreBatches || loadingBatches || isFetchingBatches.current) return;
    const nextPage = batchPage + 1;
    setBatchPage(nextPage);
    // Use current selected trainer ID (null if none)
    await loadBatches(batchSearchTerm, nextPage, true, selectedTrainerFilter?.value);
  }, [hasMoreBatches, loadingBatches, batchPage, batchSearchTerm, loadBatches, selectedTrainerFilter]);

  const handleBatchSearch = useMemo(
    () => debounce(async (inputValue) => {
      setBatchSearchTerm(inputValue);
      setBatchPage(1);
      // Use current selected trainer ID (null if none)
      await loadBatches(inputValue, 1, false, selectedTrainerFilter?.value);
    }, 700),
    [loadBatches, selectedTrainerFilter]
  );

  const handleBatchDropdownOpen = useCallback(async () => {
    // Only load if not already loaded and not currently loading
    if (!hasLoadedBatches && batchOptions.length === 0 && !loadingBatches) {
      // Use current selected trainer ID (null if none)
      await loadBatches("", 1, false, selectedTrainerFilter?.value);
      setHasLoadedBatches(true);
    }
  }, [hasLoadedBatches, batchOptions.length, loadingBatches, loadBatches, selectedTrainerFilter]);

  // ---------- Trainer selection: NO API CALL for batches ----------
  const handleTrainerSelection = async (selectedOptions) => {
    const selectedOption = selectedOptions && selectedOptions.length > 0 ? selectedOptions[0] : null;
    setSelectedTrainerFilter(selectedOption);
    // Reset batch selection
    setSelectedBatchFilter(null);
    // Reset batch options and loaded flag (so next dropdown open fetches fresh)
    setBatchOptions([]);
    setHasLoadedBatches(false);
    setBatchSearchTerm("");
    setBatchPage(1);
    // DO NOT call loadBatches here. Wait until dropdown opens.
  };

  const handleBatchSelection = (selectedOptions) => {
    const selectedOption = selectedOptions && selectedOptions.length > 0 ? selectedOptions[0] : null;
    setSelectedBatchFilter(selectedOption);
  };

  // ---------- Current user and data fetching ----------
  const fetchCurrentUser = async () => {
    try {
      const response = await getMe();
      const userData = response.data;
      setCurrentUser(userData);
      const profileLabel = userData.profiles?.[0]?.label?.toLowerCase();
      setIsTrainer(profileLabel === "trainer");
      setIsAdmin(profileLabel === "admin" || userData.isSuperAdmin);
      return userData;
    } catch {
      setCurrentUser(null);
      return null;
    }
  };

  const fetchData = useCallback(async () => {
    if (!currentUser) return;
    const isAppend = appendRef.current;
    if (isAppend) setLoadingMore(true);
    else setLoading(true);
    try {
      const profileLabel = currentUser?.profiles?.[0]?.label?.toLowerCase();
      const isTrainerUser = profileLabel === "trainer";
      const isAdminOrCrm = profileLabel === "admin" || currentUser?.isSuperAdmin || profileLabel === "crm";

      const trainerIdParam = currentUser && !currentUser.isSuperAdmin && !isAdminOrCrm && isTrainerUser
        ? String(currentUser.id)
        : appliedTrainerFilter || undefined;

      const batchIdParam = appliedBatchFilter || undefined;

      const res = await getEvents(
        recordsPerPage,
        currentPage,
        searchTerm,
        searchField,
        trainerIdParam,
        batchIdParam
      );

      const pageData = res?.data || [];
      const pagination = res?.pagination || {};
      setTotalRecords(Number(pagination.total || 0));

      const pageOffset = (Number(currentPage) - 1) * Number(recordsPerPage);
      const formattedRecords = pageData.map((item, index) => {
        const isHoliday = item.category === "Holiday";
        let dateDisplay;
        if (isHoliday) {
          const start = item.startDateTime;
          const end = item.endDateTime;
          if (start && end && start !== end) {
            dateDisplay = `${formatDateToDMY(start)} to ${formatDateToDMY(end)}`;
          } else {
            dateDisplay = formatDateToDMY(start || item.date);
          }
        } else {
          dateDisplay = formatDateToDMY(item.date);
        }
        const timeDisplay = formatTimeIST(item.time || item.startDateTime);
        const organizerName = Array.isArray(item.organizerId) && item.organizerId[0]
          ? `${item.organizerId[0].firstName || ""} ${item.organizerId[0].lastName || ""}`.trim() || item.organizerName || "N/A"
          : item.organizerName || "N/A";

        return {
          ...item,
          srno: pageOffset + index + 1,
          date: dateDisplay,
          time: timeDisplay,
          venue: getVenueNameFromPopulated(item.venueId),
          trainerName: getTrainerNameFromPopulated(item.trainerId),
          batchName: getBatchNameFromPopulated(item.batchId),
          organizerNameDisplay: organizerName,
          priority: normalizePriority(item.priority),
          originalDate: item.date,
          originalTime: item.time,
          originalStartDateTime: item.startDateTime,
          originalEndDateTime: item.endDateTime,
          createdAtFormatted: formatDateTimeWithAmPm(item.createdAt),
          updatedAtFormatted: formatDateTimeWithAmPm(item.updatedAt),
        };
      });
      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setData((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...formattedRecords.filter((r) => !seen.has(r.id))];
        });
      } else {
        setData(formattedRecords);
      }
      appendRef.current = false;
    } catch (err) {
      console.error("Error fetching events:", err);
      toast.error("Failed to fetch events");
      setData([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [appliedTrainerFilter, appliedBatchFilter, currentPage, recordsPerPage, searchTerm, searchField, currentUser]);

  // Initial load – only once
  useEffect(() => {
    if (!hasLoaded.current) {
      fetchCurrentUser();
      hasLoaded.current = true;
    }
  }, []);

  // Fetch events when filters/pagination/search change
  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [appliedTrainerFilter, appliedBatchFilter, currentPage, recordsPerPage, searchTerm, fetchData, currentUser]);

  // ---------- Batch details popup (unchanged) ----------
  const showBatchDetailsPopup = (batchName, batchId, eventId) => {
    const event = data.find((item) => item.id === eventId);
    if (!event || !event.batchId) return;
    const batches = Array.isArray(event.batchId) ? event.batchId : [event.batchId];
    const targetBatch = batches.find((b) => b.id === batchId || b.name === batchName);
    if (!targetBatch) {
      Swal.fire({
        title: "Batch Details",
        html: `<div>
          <p><strong>Batch Name:</strong> ${batchName}</p>
          <p><strong>Batch ID:</strong> ${batchId || "N/A"}</p>
        </div>`,
        confirmButtonColor: "#ff2c41",
        confirmButtonText: "Close",
        width: "500px",
      });
      return;
    }
    Swal.fire({
      title: "Batch Details",
      html: `<div>
        <table>
          <tbody>
            <tr>
              <td>Batch Name:</td>
              <td>${targetBatch.name || "N/A"}</td>
            </tr>
            <tr>
              <td>Mode:</td>
              <td>${targetBatch.mode || "N/A"}</td>
            </tr>
            <tr>
              <td>Days:</td>
              <td>${targetBatch.days || "N/A"}</td>
            </tr>
            <tr>
              <td>Start Time:</td>
              <td>${targetBatch.startTime ? formatTimeIST(targetBatch.startTime) : "N/A"}</td>
            </tr>
            <tr>
              <td>End Time:</td>
              <td>${targetBatch.endTime ? formatTimeIST(targetBatch.endTime) : "N/A"}</td>
            </tr>
            ${targetBatch.meetingLink && targetBatch.meetingLink !== "ni" ? `
            <tr>
              <td>Meeting Link:</td>
              <td>
                <a href="${targetBatch.meetingLink}" target="_blank" rel="noopener noreferrer">
                  ${targetBatch.meetingLink.length > 40 ? targetBatch.meetingLink.substring(0, 40) + "..." : targetBatch.meetingLink}
                </a>
              </td>
            </tr>` : ""}
          </tbody>
        </table>
      </div>`,
      confirmButtonText: "Close",
      confirmButtonColor: "#ff2c41",
      width: "600px",
      customClass: { popup: "batch-details-popup" },
    });
  };

  const renderBatchName = (batchNameStr, eventId) => {
    if (batchNameStr === "N/A") return "N/A";
    const arr = batchNameStr.split(", ");
    const truncatedArr = arr.map(name => name.length > 50 ? `${name.substring(0, 50)}...` : name);
    if (arr.length <= 2) {
      return truncatedArr.map((name, idx) => {
        const originalName = arr[idx];
        return (
          <span key={idx} onClick={(e) => { e.stopPropagation(); showBatchDetailsPopup(originalName, null, eventId); }}>
            {name}{idx < arr.length - 1 && ", "}
          </span>
        );
      });
    }
    const display = truncatedArr.slice(0, 2).join(", ");
    const remaining = arr.length - 2;
    return (
      <span title="more batches">
          <span onClick={(e) => {
            e.stopPropagation();
            Swal.fire({
              title: "All Batches",
              html: `<div>
                ${arr.map((name, idx) => `<div onclick="window.dispatchEvent(new CustomEvent('viewBatchDetails', { detail: { name: '${name.replace(/'/g, "\\'")}', eventId: '${eventId}' } }))">
                  ${idx + 1}. ${name}
                </div>`).join("")}
              </div>`,
              confirmButtonText: "Close",
              confirmButtonColor: "#ff2c41",
              width: "600px",
              didOpen: () => {
                window.addEventListener("viewBatchDetails", (e) => {
                  Swal.close();
                  showBatchDetailsPopup(e.detail.name, null, eventId);
                });
              },
            });
          }}>{display}</span>
          <span onClick={(e) => {
            e.stopPropagation();
            Swal.fire({
              title: "All Batches",
              html: `<div>
                ${arr.map((name, idx) => `<div onclick="window.dispatchEvent(new CustomEvent('viewBatchDetails', { detail: { name: '${name.replace(/'/g, "\\'")}', eventId: '${eventId}' } }))">
                  ${idx + 1}. ${name}
                </div>`).join("")}
              </div>`,
              confirmButtonText: "Close",
              confirmButtonColor: "#ff2c41",
              width: "600px",
              didOpen: () => {
                window.addEventListener("viewBatchDetails", (e) => {
                  Swal.close();
                  showBatchDetailsPopup(e.detail.name, null, eventId);
                });
              },
            });
          }}>+{remaining} more</span>
        </span>
    );
  };

  // ---------- Filter handlers ----------
  const handleApplyFilters = () => {
    appendRef.current = false;
    setAppliedTrainerFilter(selectedTrainerFilter?.value || "");
    setAppliedBatchFilter(selectedBatchFilter?.value || "");
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    appendRef.current = false;
    setSelectedTrainerFilter(null);
    setSelectedBatchFilter(null);
    setAppliedTrainerFilter("");
    setAppliedBatchFilter("");
    setCurrentPage(1);
    // Reset batch dropdown state (but do not fetch now)
    setBatchOptions([]);
    setHasLoadedBatches(false);
    setBatchSearchTerm("");
    setBatchPage(1);
    // Also reset trainer search if needed (optional)
  };

  const hasMore = data.length < totalRecords;

  // The foot of the list asks for the next page when it comes into view.
  useEffect(() => {
    const node = listEndRef.current;
    if (!node || !hasMore || loading) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        // `loading` is state and lags a frame, so the ref is what stops one
        // pass asking for two pages.
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        appendRef.current = true;
        setLoadingMore(true);
        setCurrentPage((p) => p + 1);
      },
      { rootMargin: "150px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loading, data.length]);

  const handlePageChange = (page) => {
    if (page !== currentPage) { appendRef.current = false; setCurrentPage(page); }
  };
  const handleRowsPerPageChange = (newLimit) => {
    appendRef.current = false; setRecordsPerPage(newLimit); setCurrentPage(1);
  };
  /* Typing does not fire a request per keystroke any more -- the box
     and the field beside it still move at once, and the fetch
     underneath waits for the typing to stop. */
  const runSearch = useDebouncedCallback((value) => {
    appendRef.current = false;
    setSearchTerm(value);
    setCurrentPage(1);
  });

  const onSearchChange = (value, field) => {
    setUiSearchTerm(value);
    setSearchField(field);
    runSearch(value);
  };

  const handleDelete = async (id) => {
    const answer = await confirm({
      title: "Delete Confirmation",
      text: "Are you sure you want to delete this Event?",
      tone: 'danger',
      confirmButtonText: "Yes, Delete it!",
    });
    if (answer.isConfirmed) {
      try {
        await deleteEvent(id);
        // The view popup says so in place, then closes itself.
        toast.success("Event deleted successfully");
        fetchData();
      } catch {
        toast.error("Failed to delete event");
      }
    }
  };

  const headers = [
    {
      title: "Event",
      prop: "name",
      sortable: true,
      cell: (row) => (
        <div className="lp-cell">
          <span className="lp-name">{row.name}</span>
          {row.category && <span className="lp-sub">{row.category}</span>}
        </div>
      ),
    },
    {
      title: "When",
      prop: "date",
      sortable: true,
      cell: (row) => (
        <div className="lp-cell">
          <span className="lp-when">{row.date || "—"}</span>
          {row.time && <span className="lp-sub">{row.time}</span>}
        </div>
      ),
    },
    {
      title: "Where",
      prop: "venue",
      cell: (row) => (
        <div className="lp-cell">
          <span className="lp-plain">{row.venue || "—"}</span>
          {row.classroomNumber && row.classroomNumber !== "N/A" && (
            <span className="lp-sub">Room {row.classroomNumber}</span>
          )}
        </div>
      ),
    },
    {
      title: "Trainers",
      prop: "trainerName",
      cell: (row) => {
        if (!row.trainerName || row.trainerName === "N/A") {
          return <span className="lp-muted">—</span>;
        }
        // Two named, the rest counted, so one event covering twenty
        // trainers cannot push every other column off the table.
        const names = row.trainerName.split(", ");
        return (
          <span className="lp-plain">
            {names.slice(0, 2).join(", ")}
            {names.length > 2 && (
              <span className="lp-more">+{names.length - 2} more</span>
            )}
          </span>
        );
      },
    },
    {
      title: "Priority",
      prop: "priority",
      sortable: true,
      cell: (row) => {
        const meta = getPriorityMeta(row.priority);
        return (
          <span
            className="cal-tag cal-tag--prio"
            style={{
              color: meta.color,
              background: meta.bg,
              borderColor: meta.border,
            }}
          >
            {meta.label}
          </span>
        );
      },
    },
  ];

  const searchableFields = [
    { value: "name", label: "Event Name" },
    { value: "category", label: "Category" },
    { value: "priority", label: "Priority" },
    { value: "organizerName", label: "Organizer" },
    { value: "classroomNumber", label: "Classroom" },
  ];

  // Split once per render rather than four times inside the markup.
  const trainerList = splitNames(details?.trainerName);
  const batchList = splitNames(details?.batchName);

  const canEdit = hasPermission("list-event-form", "update", "lms");
  const canDelete = hasPermission("list-event-form", "delete", "lms");

  // A change saved in a popup is confirmed in that popup. The form, opened
  // over the view, is the newer of the two, so it takes its own save.
  const { done: detailsDone } = usePopupDone(Boolean(details));
  const { done: formDone } = usePopupDone(Boolean(formModal));

  return (
    <div className="pg lp">

      <header className="pg-head pg-head--bare">
        <div className="pg-head-actions">
          {hasPermission("list-event-form", "add", "lms") && (
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => setFormModal({ recordId: null })}
            >
              Add event
            </button>
          )}
        </div>
      </header>

      <FilterSheet label="Filter events">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="lp-ev-search">
              Search
            </label>
            <input
              id="lp-ev-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by event, category, organiser or classroom..."
              value={uiSearchTerm}
              onChange={(e) => onSearchChange(e.target.value, searchField)}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="lp-ev-field">
              Search in
            </label>
            <select
              id="lp-ev-field"
              className="pg-select"
              value={searchField}
              onChange={(e) => onSearchChange(uiSearchTerm, e.target.value)}
            >
              {searchableFields.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="lp-ev-rows">
              Rows per load
            </label>
            <select
              id="lp-ev-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {isAdmin && (
          <div className="pg-filters-row">
            <div className="pg-filters-fields pg-filters-fields--notrainer">
              <div className="pg-field">
                <label className="pg-label">Trainer</label>
                <NewCustomAsyncSelectDropdown
                  options={trainerOptions}
                  value={selectedTrainerFilter ? [selectedTrainerFilter] : []}
                  onChange={handleTrainerSelection}
                  placeholder="All trainers"
                  isMulti={false}
                  isSearchable={true}
                  searchInControl={true}
                  isLoading={loadingTrainers}
                  onMenuOpen={handleTrainerDropdownOpen}
                  onInputChange={handleTrainerSearch}
                  onMenuScrollToBottom={handleLoadMoreTrainers}
                />
              </div>

              <div className="pg-field">
                <label className="pg-label">Batch</label>
                <NewCustomAsyncSelectDropdown
                  options={batchOptions}
                  value={selectedBatchFilter ? [selectedBatchFilter] : []}
                  onChange={handleBatchSelection}
                  placeholder="All batches"
                  isMulti={false}
                  isSearchable={true}
                  searchInControl={true}
                  isLoading={loadingBatches}
                  onMenuOpen={handleBatchDropdownOpen}
                  onInputChange={handleBatchSearch}
                  onMenuScrollToBottom={handleLoadMoreBatches}
                />
              </div>
            </div>

            <div className="pg-filters-actions">
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={handleApplyFilters}
              >
                Apply
              </button>
              <button
                type="button"
                className="pg-btn"
                onClick={handleClearFilters}
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </FilterSheet>

      <div className="td-list td-list--lms">
        <NewCustomDataTable
          data={data}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          serverSide={true}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => {
            setDetailsTab("details");
            setDetails(row);
          }}
          validRowClickTagNames="*"
        />
      </div>

      <div className="att-picklist-foot" ref={listEndRef}>
        {loadingMore ? (
          <>
            <span className="pg-spinner att-spinner" aria-hidden="true" />
            <span>Loading more events...</span>
          </>
        ) : hasMore ? (
          <span>
            {data.length} of {totalRecords} events
          </span>
        ) : (
          totalRecords > 0 && (
            <span>
              All {totalRecords} event{totalRecords === 1 ? "" : "s"} loaded
            </span>
          )
        )}
      </div>

      {/* Adding an event is three steps -- what it is, who it reaches,
          where it happens -- so the dialog carries the step strip. */}
      {formModal && (
        <div
          className="pg-modal pg-modal--over"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-ev-form-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setFormModal(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--xl pg-modal-panel--form">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Event</span>
                <h3 className="pg-modal-title" id="lp-ev-form-title">
                  {formModal.recordId ? "Edit event" : "Add an event"}
                </h3>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setFormModal(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {formDone ? (
                <PopupDone message={formDone} onClose={() => setFormModal(null)} />
              ) : (
                <AddEventModal
                  selectedEventId={formModal.recordId}
                  selectedDate={null}
                  userRole={isAdmin ? "admin" : "trainer"}
                  onCancel={() => setFormModal(null)}
                  onEventSaved={() => {
                    // Saved: the dialog shows so in place and closes itself.
                    // The record changed, so the view popup still open behind this form
                    // would be showing the old one. It goes; the form stays.
                    setDetails(null);
                    appendRef.current = false;
                    setCurrentPage(1);
                    fetchData();
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* One event, in three tabs. Fourteen columns is not a table anybody
          reads, and trainers and batches are lists in their own right --
          they get a tab each rather than being stacked under the facts. */}
      {details && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-ev-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setDetails(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--xl">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Event</span>
                <h3 className="pg-modal-title" id="lp-ev-title">
                  {details.name}
                </h3>
                {details.category && (
                  <p className="pg-modal-sub">{details.category}</p>
                )}
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setDetails(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            {detailsDone ? (
              <div className="pg-modal-body">
                <PopupDone message={detailsDone} onClose={() => setDetails(null)} />
              </div>
            ) : (
            <>
            <div className="lp-tabs" role="tablist" aria-label="Event">
              {[
                { key: "details", label: "Details" },
                { key: "trainers", label: "Trainers", n: trainerList.length },
                { key: "batches", label: "Batches", n: batchList.length },
              ].map((t) => (
                <button
                  type="button"
                  key={t.key}
                  role="tab"
                  id={`lp-ev-tab-${t.key}`}
                  aria-selected={detailsTab === t.key}
                  aria-controls={`lp-ev-panel-${t.key}`}
                  className={`lp-tab${detailsTab === t.key ? " is-on" : ""}`}
                  onClick={() => setDetailsTab(t.key)}
                >
                  {t.label}
                  {t.n !== undefined && <span className="lp-tab-n">{t.n}</span>}
                </button>
              ))}
            </div>

            <div className="pg-modal-body">
              {detailsTab === "details" && (
                <div
                  role="tabpanel"
                  id="lp-ev-panel-details"
                  aria-labelledby="lp-ev-tab-details"
                >
                  <dl className="quiz-facts">
                    <div>
                      <dt>Priority</dt>
                      <dd>{getPriorityMeta(details.priority).label}</dd>
                    </div>
                    <div>
                      <dt>Date</dt>
                      <dd>{details.date || "—"}</dd>
                    </div>
                    <div>
                      <dt>Time</dt>
                      <dd>{details.time || "—"}</dd>
                    </div>
                    <div>
                      <dt>Venue</dt>
                      <dd>{details.venue || "—"}</dd>
                    </div>
                    <div>
                      <dt>Classroom</dt>
                      <dd>{details.classroomNumber || "—"}</dd>
                    </div>
                    <div>
                      <dt>Organiser</dt>
                      <dd>{details.organizerNameDisplay || "—"}</dd>
                    </div>
                    <div>
                      <dt>Added</dt>
                      <dd>{details.createdAtFormatted || "—"}</dd>
                    </div>
                    <div>
                      <dt>Updated</dt>
                      <dd>{details.updatedAtFormatted || "—"}</dd>
                    </div>
                  </dl>

                  {details.description && (
                    <>
                      <h5 className="pgm-section-title">Description</h5>
                      <p className="lp-prose">{details.description}</p>
                    </>
                  )}
                </div>
              )}

              {detailsTab === "trainers" && (
                <div
                  role="tabpanel"
                  id="lp-ev-panel-trainers"
                  aria-labelledby="lp-ev-tab-trainers"
                >
                  {trainerList.length === 0 ? (
                    <p className="lp-muted">No trainer is on this event.</p>
                  ) : (
                    <div className="lp-dialog-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Sr. No</th>
                            <th>Trainer</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trainerList.map((name, i) => (
                            <tr key={`${name}-${i}`}>
                              <td>{i + 1}</td>
                              <td>{name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {detailsTab === "batches" && (
                <div
                  role="tabpanel"
                  id="lp-ev-panel-batches"
                  aria-labelledby="lp-ev-tab-batches"
                >
                  {batchList.length === 0 ? (
                    <p className="lp-muted">No batch is on this event.</p>
                  ) : (
                    <div className="lp-dialog-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Sr. No</th>
                            <th>Batch</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batchList.map((name, i) => (
                            <tr key={`${name}-${i}`}>
                              <td>{i + 1}</td>
                              <td>{name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>

            <footer className="pg-modal-foot">
              <div className="pgm-actions">
                {canEdit && (
                  <button
                    type="button"
                    className="pgm-btn"
                    onClick={() => {
                      const id = details.id;
                      setFormModal({ recordId: id });
                    }}
                  >
                    Edit event
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="pgm-btn pgm-btn--danger"
                    onClick={() => {
                      const id = details.id;
                      handleDelete(id);
                    }}
                  >
                    Delete event
                  </button>
                )}
              </div>
              <button
                type="button"
                className="pg-btn pg-btn--main"
                onClick={() => setDetails(null)}
              >
                Close
              </button>
            </footer>
            </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ListEvent;
